import uuid4 from 'uuid/v4';
import underscored from 'underscore.string/underscored';
import camelize from 'underscore.string/camelize';
import capitalize from 'underscore.string/capitalize';

import {messagesHaveLevel} from './utils';

const required = 0b01;
const optional = 0b00;
const noTrim = 0b10;
const MESSAGE_LEVELS = ['error', 'warning', 'info'];

export {required, optional, noTrim};

export default class ModelProxy {

  constructor(data) {
    this.props = {};
    this._messages = {};

    const self = new Proxy(this, {
      get: (target, key) => {
        if (key === '__class__') {
          return this.constructor;
        }
        if (key === '__props__') {
          return target.props;
        }
        let getFuncName = null;
        let defaults = target.constructor.defaultProps || {};
        if (typeof key === 'string' && !(key.startsWith('_'))) {
          key = camelize(key);
          getFuncName = `get${capitalize(key)}`;
        }
        if (getFuncName in target.constructor) {
          return target.constructor[getFuncName](target);
        }
        else if (key in target) {
          return target[key];
        }
        else if (typeof target.props[key] !== 'undefined') {
          return target.props[key];
        }
        else {
          return defaults[key];
        }
      },
      set: (target, key, value) => {
        let setFuncName = null;
        if (!(key.startsWith('_'))) {
          key = camelize(key);
          setFuncName = `set${capitalize(key)}`;
        }
        if (setFuncName in target.constructor) {
          target.constructor[setFuncName](target, value);
        }
        else if (key in target) {
          target[key] = value;
        }
        else {
          target.props[key] = value;
        }
        return true;
      },
      deleteProperty: (target, key) => {
        if (!(key.startsWith('_'))) {
          key = camelize(key);
        }
        if (key in target) {
          return delete target[key];
        }
        else {
          return delete target.props[key];
        }
      },
      has: (target, key) => {
        if (!(key.startsWith('_'))) {
          key = camelize(key);
        }
        return key in target || key in target.props;
      },
      ownKeys: target => {
        return Object.keys(target.props);
      }
    });
    self.uuid = uuid4();
    self.type = this.constructor.name;
    if (data) {
      for (const [key, val] of Object.entries(data)) {
        self[key] = val;
      }
    }
    return self;
  }

  static trim(value) {
    if (typeof(value) === "string") {
      return value.trim().replace(/^,+|,+$/, '');
    }
    return value;
  }

  static parse(jsonstring) {
    const data = JSON.parse(jsonstring);
    const self = new this();
    for (const [key, value] of Object.entries(data)) {
      self[key] = value;
    }
    return self;
  }

  stringify(use_underscored_name = false) {
    if (!use_underscored_name) {
      return JSON.stringify(this);
    }
    const data = {};
    for (const [key, value] of Object.entries(this)) {
      data[underscored(key)] = value;
    }
    return JSON.stringify(data);
  }

  exportData() {
    const data = {};
    for (const key of Object.keys(this.constructor.propTypes)) {
      let val = this[key];
      if (val) {
        if (val.exportData) {
          val = val.exportData();
        }
        else if (val instanceof Array) {
          val = val.map(v => v.exportData ? v.exportData() : v);
        }
      }
      data[key] = val;
    }
    return data;
  }

  toJSON() {
    return this.props;
  }

  isEmpty() {
    const defaults = this.constructor.defaultProps || {};
    for (const key of Object.keys(this.constructor.propTypes)) {
      if (key === 'uuid') {
        continue;
      }
      if (this[key] && this[key] !== defaults[key]) {
        return false;
      }
    }
    return true;
  }

  hasMessage() {
    return Object.keys(this._messages).length > 0;
  }

  addMessage(key, message) {
    this._messages[key] = this._messages[key] || [];
    this._messages[key].push(message);
  }

  clearMessage() {
    this._messages = {};
  }

  messages(key,
    returns_errors = true,
    returns_info = false,
    returns_warnings = false) {
    if (Object.keys(this._messages).length === 0) {
      this.validate();
    }
    let msgs;
    if (key) {
      msgs = this._messages[key] || [];
    } else {
      msgs = Object.values(this._messages)
        .reduce((acc, v) => acc.concat(v), []);
    }
    if (!returns_errors) {
      msgs = msgs.filter(({level}) => level !== 'error');
    }
    if (!returns_info) {
      msgs = msgs.filter(({level}) => level !== 'info');
    }
    if (!returns_warnings) {
      msgs = msgs.filter(({level}) => level !== 'warning');
    }

    // TODO: standalone function.
    const message_type_statistics = msgs.reduce((acc, msg) => {
      const {type} = msg;
      if (type) {
        acc[type] = acc[type] || 0;
        acc[type] = acc[type] + 1;
      }
      return acc;
    }, {});

    const multiple_message_types = Object.keys(
      message_type_statistics).filter((type_name) => {
      return message_type_statistics[type_name] > 1;
    });

    const simple_messages = msgs.filter((msg) => {
      if (msg.type && multiple_message_types.includes(msg.type)) {
        return false;
      } else {
        return true;
      }
    });

    let complex_messages = msgs.filter((msg) => {
      if (msg.type && multiple_message_types.includes(msg.type)) {
        return true;
      } else {
        return false;
      }
    });

    complex_messages = complex_messages.reduce((acc, msg) => {
      const {type_name} = msg.type;
      if (!acc[type_name]) {
        acc[type_name] = {
          text: msg.group_text || 'error',
          level: msg.level,
          property: msg.property || null,
        };
      }
      return acc;
    }, {});
    complex_messages = Object.values(complex_messages);

    msgs = simple_messages.concat(complex_messages);

    msgs = msgs.sort((msg1, msg2) => {
      if (msg1.property === 'ptAlias') {
        return -1;
      } else if (msg2.property === 'ptAlias') {
        return 1;
      }
      if (msg1.property) {
        return -1;
      } else if(msg2.property) {
        return 1;
      }
      const l1 = MESSAGE_LEVELS.indexOf(msg1.level);
      const l2 = MESSAGE_LEVELS.indexOf(msg2.level);
      return l1 - l2;
    });
    return msgs;
  }

  validate() {
    this._messages = {};
    let hasError = false;
    let hasWarning = false;
    for (const [key, flag] of Object.entries(this.constructor.propTypes)) {
      const val = this[key];
      let msgs = [];
      if ((flag & required) && (val === null || val === undefined)) {
        msgs.push({
          text: `Property "${key}" can not be empty.`,
          type: 'property required',
          group_text: `Property "${key}" can not be empty.`,
          property: key,
          level: 'error'
        });
        hasError = true;
      }
      if (!(flag & noTrim) && val && val.trim && val.trim().length < val.length) {
        msgs.push({
          text: `Property "${key}" contains leading/trailing spaces. Please fix it.`,
          level: 'error'
        });
        hasError = true;
      }
      const validateFuncName = `validate${capitalize(key)}`;
      if (validateFuncName in this.constructor) {
        msgs = msgs.concat(this.constructor[validateFuncName](this, val));

      }
      if (msgs.length > 0) {
        this._messages[key] = msgs;
        hasError = hasError || messagesHaveLevel(msgs, 'error');
        hasWarning = hasWarning || messagesHaveLevel(msgs, 'warning');
      }
    }
    this.hasError = hasError;
    this.hasWarning = hasWarning;
    return !hasError;
  }

}



// WEBPACK FOOTER //
// ./src/models/proxy.js